import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { RootFocusedAnnotationService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/root-focused-annotation.service';
import { ObjectClassService } from '../../../../../../../core/shared/misc-services/object-class.service';
import { SidePanelDisplayService } from '../../../../side-panel/side-panel-display.service';

@Component({
  selector: 'h3-annotation-edit-pane',
  templateUrl: './annotation-edit-pane.component.html',
  styleUrls: ['./annotation-edit-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnnotationEditPaneComponent
  extends DirectiveSuperclass
  implements OnInit
{
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  shouldShowClassSelector$ = this.taskWorkspaceStore('sidePanel')(
    'annotationPanelState',
  ).$.pipe(map((state) => state === 'class_selector'));
  isFocusedStoreAvailable$!: Observable<boolean>;
  paneTitle$!: Observable<string>;
  isTaxonomyEmpty!: boolean;
  hasValidClass$!: Observable<boolean>;

  protected isClassReadOnly$!: Observable<boolean>;

  private rootFocusedAnnotationService = inject(RootFocusedAnnotationService);
  private sidePanelDisplayService = inject(SidePanelDisplayService);
  private objectClassService = inject(ObjectClassService);

  ngOnInit() {
    // Required to support Objects P0 and P1 tests
    this.isTaxonomyEmpty = !this.taskWorkspaceStore.hasTaxonomy();

    this.isFocusedStoreAvailable$ =
      this.rootFocusedAnnotationService.isAnnotationFocused$();

    this.subscribeToFocusedStoreAvailable();

    this.paneTitle$ = this.buildPaneTitle();
    this.hasValidClass$ = this.rootFocusedAnnotationService
      .getClass$()
      .pipe(
        map((className) => this.objectClassService.isValidClass(className)),
      );
    this.isClassReadOnly$ = this.taskWorkspaceStore('readOnly').$.pipe(
      map((isReadOnly) => this.taskWorkspaceStore.isUsingGenAi() || isReadOnly),
    );
  }

  openClassSelector(source: string) {
    if (
      this.taskWorkspaceStore.state().readOnly ||
      this.taskWorkspaceStore.isUsingGenAi()
    ) {
      return;
    }

    this.sidePanelDisplayService.showClassSelector(source);
  }

  closeClassSelector(source?: string) {
    this.sidePanelDisplayService.hideClassSelector(source);
  }

  private subscribeToFocusedStoreAvailable() {
    this.subscribeTo(
      this.isFocusedStoreAvailable$,
      (isFocusedStoreAvailable) => {
        if (!isFocusedStoreAvailable) {
          this.closeClassSelector('cleared_selection');
        }
      },
    );
  }

  private buildPaneTitle() {
    return this.rootFocusedAnnotationService
      .getClass$()
      .pipe(
        map(
          (className) =>
            this.objectClassService.getClassLabel(className) ??
            'Unspecified class',
        ),
      );
  }
}
